/** API client with typed error handling, timeout, and auth header injection. */

export class ApiError extends Error {
  readonly code: string;
  readonly statusCode: number;
  readonly details?: unknown;

  constructor(code: string, statusCode: number, message: string, details?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.code = code;
    this.statusCode = statusCode;
    this.details = details;
  }
}

interface ApiErrorBody {
  error: {
    code: string;
    message: string;
    details?: unknown;
  };
}

/** Global 401 handler hook. Registered once (typically in main.ts) so the app
 * can transition from logged-in to logged-out state when any /api/v1/* request
 * returns 401 mid-session. The handler is invoked before the ApiError is
 * thrown, so callers' .catch() still runs as before.
 */
let unauthenticatedHandler: (() => void) | null = null;

export function setUnauthenticatedHandler(handler: () => void): void {
  unauthenticatedHandler = handler;
}

/**
 * CSRF token provider. Registered once after sign-in so the API client can
 * attach `X-CSRF-Token` to every state-changing (POST/PUT/PATCH/DELETE)
 * request. Returns null when no session is established yet (in which case
 * the request will likely 401 anyway).
 */
let csrfTokenProvider: (() => string | null) | null = null;

export function setCsrfTokenProvider(provider: () => string | null): void {
  csrfTokenProvider = provider;
}

const MUTATION_METHODS = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

interface RequestOptions {
  body?: unknown;
  query?: Record<string, string | number | boolean | undefined>;
  timeout?: number;
  signal?: AbortSignal;
}

function buildQueryString(params: Record<string, string | number | boolean | undefined>): string {
  const entries: string[] = [];
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined) continue;
    entries.push(`${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`);
  }
  return entries.length > 0 ? `?${entries.join('&')}` : '';
}

function isApiErrorBody(data: unknown): data is ApiErrorBody {
  return (
    typeof data === 'object' &&
    data !== null &&
    'error' in data &&
    typeof (data as ApiErrorBody).error === 'object' &&
    (data as ApiErrorBody).error !== null &&
    typeof (data as ApiErrorBody).error.code === 'string' &&
    typeof (data as ApiErrorBody).error.message === 'string'
  );
}

export interface ApiClient {
  readonly baseUrl: string;
  readonly apiKey: string;
  get<T>(path: string, opts?: RequestOptions): Promise<T>;
  post<T>(path: string, opts?: RequestOptions): Promise<T>;
  put<T>(path: string, opts?: RequestOptions): Promise<T>;
  patch<T>(path: string, opts?: RequestOptions): Promise<T>;
  del<T>(path: string, opts?: RequestOptions): Promise<T>;
}

export function createApiClient(config: {
  baseUrl: string;
  apiKey: string;
  defaultTimeout?: number;
}): ApiClient {
  const { baseUrl, apiKey, defaultTimeout = 10_000 } = config;

  async function request<T>(method: string, path: string, opts?: RequestOptions): Promise<T> {
    const qs = opts?.query ? buildQueryString(opts.query) : '';
    const url = `${baseUrl}${path}${qs}`;

    const controller = new AbortController();
    const timeoutMs = opts?.timeout ?? defaultTimeout;
    const timer = setTimeout(() => controller.abort(), timeoutMs);

    const headers: Record<string, string> = {
      'X-API-Key': apiKey,
    };

    if (opts?.body !== undefined) {
      headers['Content-Type'] = 'application/json';
    }

    if (MUTATION_METHODS.has(method)) {
      const csrf = csrfTokenProvider?.();
      if (csrf) {
        headers['X-CSRF-Token'] = csrf;
      }
    }

    let response: Response;
    try {
      response = await fetch(url, {
        method,
        headers,
        body: opts?.body !== undefined ? JSON.stringify(opts.body) : undefined,
        signal: opts?.signal ?? controller.signal,
      });
    } catch (err) {
      clearTimeout(timer);
      if (err instanceof DOMException && err.name === 'AbortError') {
        throw new ApiError('TIMEOUT', 0, `Request timed out after ${timeoutMs}ms`);
      }
      throw new ApiError('NETWORK_ERROR', 0, 'Network request failed');
    } finally {
      clearTimeout(timer);
    }

    if (response.status === 429) {
      const retryAfter = response.headers.get('retry-after');
      throw new ApiError(
        'RATE_LIMIT',
        429,
        `Rate limited${retryAfter ? `. Retry after ${retryAfter}s` : ''}`,
        retryAfter ? { retryAfter: Number(retryAfter) } : undefined,
      );
    }

    if (!response.ok) {
      if (response.status === 401) {
        unauthenticatedHandler?.();
      }

      let body: unknown;
      try {
        body = await response.json();
      } catch {
        throw new ApiError(
          'UNKNOWN',
          response.status,
          `HTTP ${response.status}: ${response.statusText}`,
        );
      }

      if (isApiErrorBody(body)) {
        // Backend routes occasionally attach metadata as siblings of
        // `code`/`message` on `error` (e.g. `existing_rule`,
        // `transaction_count`, `rule_count`, `count`, `ceiling`). Forward
        // an explicit `details` when present, otherwise capture all
        // unknown sibling fields so downstream consumers can read them
        // off `ApiError.details`.
        const err = body.error as Record<string, unknown>;
        let details: unknown = err['details'];
        if (details === undefined) {
          const KNOWN = new Set(['code', 'message', 'details']);
          const extras: Record<string, unknown> = {};
          let hasExtra = false;
          for (const [k, v] of Object.entries(err)) {
            if (KNOWN.has(k)) continue;
            extras[k] = v;
            hasExtra = true;
          }
          if (hasExtra) details = extras;
        }
        throw new ApiError(body.error.code, response.status, body.error.message, details);
      }

      throw new ApiError(
        'UNKNOWN',
        response.status,
        `HTTP ${response.status}: ${response.statusText}`,
      );
    }

    // 204 No Content
    if (response.status === 204) {
      return undefined as T;
    }

    return (await response.json()) as T;
  }

  return {
    baseUrl,
    apiKey,
    get: <T>(path: string, opts?: RequestOptions) => request<T>('GET', path, opts),
    post: <T>(path: string, opts?: RequestOptions) => request<T>('POST', path, opts),
    put: <T>(path: string, opts?: RequestOptions) => request<T>('PUT', path, opts),
    patch: <T>(path: string, opts?: RequestOptions) => request<T>('PATCH', path, opts),
    del: <T>(path: string, opts?: RequestOptions) => request<T>('DELETE', path, opts),
  };
}

/** Build query string from params, skipping undefined values. Exported for testing. */
export { buildQueryString };
